import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck, ShieldAlert, Cloud, Server, HardDrive, RefreshCw, ChevronDown, ChevronRight,
  FlaskConical, AlertTriangle, Clock,
} from 'lucide-react';
import api from '../../utils/api';

// "Backups — todo": el estado de TODOS los backups (helpdesk, Medisoft por
// consultorio, casillas, node2, Canciller/DinamarcaOS, WordPress) mirando lo que
// HAY en cada destino — B2, FTP de HostMonster, copias locales —, no lo que dice
// cada script. Fuente: scripts/backup-inventory.py (node1, cada hora) →
// GET /api/backups/overview (sólo Andy, también candado server-side).
const ANDY_USER_ID = 'eafdff80-02d2-4eef-9e5c-19f57761b85d';

export const meta = {
  id: 'backups_overview',
  category: 'system',
  titleKey: 'dashboard.widgets.backupsOverview',
  descriptionKey: 'dashboard.widgets.backupsOverviewDesc',
  defaultSize: 'xl',
  ownerUserId: ANDY_USER_ID,
  icon: ShieldCheck,
};

const STATUS_COLOR = {
  ok: 'var(--c-resolved)',
  warning: 'var(--c-pending)',
  stale: 'var(--c-pending)',
  fail: 'var(--c-danger)',
  unknown: 'var(--c-text-muted)',
};
const KIND_ICON = { b2: Cloud, ftp: Server, local: HardDrive };

function fmtBytes(n) {
  if (n == null || isNaN(n)) return '—';
  if (n < 1024) return `${n} B`;
  const u = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024; let i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v >= 100 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
}

function useAgo() {
  const { t, i18n } = useTranslation();
  const rtf = new Intl.RelativeTimeFormat(i18n.language === 'en' ? 'en' : 'es', { numeric: 'auto' });
  return (iso) => {
    const ts = Date.parse(iso || '');
    if (!Number.isFinite(ts)) return t('dashboard.widgets.backupsNever');
    const mins = Math.round((Date.now() - ts) / 60000);
    if (Math.abs(mins) < 60) return rtf.format(-mins, 'minute');
    const hrs = Math.round(mins / 60);
    if (Math.abs(hrs) < 48) return rtf.format(-hrs, 'hour');
    return rtf.format(-Math.round(hrs / 24), 'day');
  };
}

function Dot({ status, size = 9 }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: STATUS_COLOR[status] || STATUS_COLOR.unknown,
      boxShadow: status === 'fail' ? `0 0 0 3px color-mix(in srgb, ${STATUS_COLOR.fail} 25%, transparent)` : 'none',
    }} />
  );
}

function StatusLabel({ status }) {
  const { t } = useTranslation();
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLOR[status] || STATUS_COLOR.unknown }}>
      {t(`dashboard.widgets.backupsStatus.${status}`)}
    </span>
  );
}

function DestChip({ d }) {
  const { t } = useTranslation();
  const Icon = KIND_ICON[d.kind] || HardDrive;
  const color = STATUS_COLOR[d.state] || STATUS_COLOR.unknown;
  const title = [d.label, d.copies != null ? `${d.copies} ${d.unit || t('dashboard.widgets.backupsCopies')}` : null, d.bytes ? fmtBytes(d.bytes) : null, d.retention]
    .filter(Boolean).join(' · ');
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 999,
      fontSize: 11, border: `1px solid ${color}`, color, background: 'var(--c-surface)', whiteSpace: 'nowrap',
    }}>
      <Icon size={11} /> {d.kind === 'b2' ? 'B2' : d.kind === 'ftp' ? 'FTP' : 'Local'}
    </span>
  );
}

function StorageCard({ storage }) {
  const { t } = useTranslation();
  const ago = useAgo();
  const ftp = storage?.ftp;
  const cap = ftp?.cap_bytes || 30 * 1024 ** 3;
  const pct = ftp?.total_bytes ? Math.min(100, Math.round((ftp.total_bytes / cap) * 100)) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
      <div style={{ border: '1px solid var(--c-border)', borderRadius: 10, padding: 12, background: 'var(--c-surface-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--c-text-muted)' }}>
          <Cloud size={14} /> {t('dashboard.widgets.backupsB2')}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, margin: '4px 0' }}>
          {fmtBytes(storage?.b2_total_bytes)}
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text-muted)', marginLeft: 8 }}>
            ≈ USD {(storage?.b2_usd_month ?? 0).toFixed(2)}{t('dashboard.widgets.backupsPerMonth')}
          </span>
        </div>
        {(storage?.b2 || []).map(b => (
          <div key={b.bucket} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--c-text-muted)' }}>
            <span style={{ fontFamily: 'var(--font-mono)' }}>{b.bucket}</span>
            <span>{fmtBytes(b.bytes)}</span>
          </div>
        ))}
      </div>
      <div style={{ border: '1px solid var(--c-border)', borderRadius: 10, padding: 12, background: 'var(--c-surface-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--c-text-muted)' }}>
          <Server size={14} /> {t('dashboard.widgets.backupsFtp')}
          {ftp && <span style={{ marginLeft: 'auto' }}><StatusLabel status={ftp.status} /></span>}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, margin: '4px 0' }}>
          {fmtBytes(ftp?.total_bytes)}
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text-muted)', marginLeft: 8 }}>
            {t('dashboard.widgets.backupsFtpCap', { cap: fmtBytes(cap) })}
          </span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: 'var(--c-border)', overflow: 'hidden', margin: '6px 0' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: STATUS_COLOR[ftp?.status] || STATUS_COLOR.unknown }} />
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--c-text-muted)' }}>
          {(ftp?.breakdown || []).filter(x => x.bytes > 50 * 1024 ** 2).slice(0, 4).map(x => `${x.label} ${fmtBytes(x.bytes)}`).join(' · ')}
          {ftp?.measured_at && <div>{t('dashboard.widgets.backupsMeasured', { ago: ago(ftp.measured_at) })}</div>}
        </div>
      </div>
    </div>
  );
}

function JobRow({ job, open, onToggle }) {
  const { t } = useTranslation();
  const ago = useAgo();
  return (
    <div style={{ borderTop: '1px solid var(--c-border)' }}>
      <div onClick={onToggle} role="button" tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter') onToggle(); }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 4px', cursor: 'pointer', flexWrap: 'wrap' }}>
        {open ? <ChevronDown size={14} color="var(--c-text-muted)" /> : <ChevronRight size={14} color="var(--c-text-muted)" />}
        <Dot status={job.status} />
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{job.label}</div>
          <div style={{ fontSize: 11.5, color: 'var(--c-text-muted)' }}>
            {job.group} · {job.schedule}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {(job.destinations || []).map((d, i) => <DestChip key={i} d={d} />)}
        </div>
        <div style={{ width: 120, textAlign: 'right', fontSize: 12 }}>
          <div><StatusLabel status={job.status} /></div>
          <div style={{ color: 'var(--c-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <Clock size={11} /> {ago(job.last_run_at)}
          </div>
        </div>
      </div>
      {open && (
        <div style={{ padding: '0 4px 12px 32px' }}>
          {(job.reasons || []).filter(r => r !== 'stale' || job.status === 'stale').length > 0 && job.status !== 'ok' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: STATUS_COLOR[job.status], marginBottom: 8 }}>
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                {(job.reasons || []).map(r => t(`dashboard.widgets.backupsReason.${r}`)).join(' · ')}
                {job.warning ? ` — ${job.warning}` : ''}
                {job.last_error ? ` — ${job.last_error}` : ''}
              </span>
            </div>
          )}
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>{t('dashboard.widgets.backupsCol.destination')}</th>
                  <th>{t('dashboard.widgets.backupsCol.status')}</th>
                  <th>{t('dashboard.widgets.backupsCol.last')}</th>
                  <th>{t('dashboard.widgets.backupsCol.copies')}</th>
                  <th>{t('dashboard.widgets.backupsCol.size')}</th>
                  <th>{t('dashboard.widgets.backupsCol.retention')}</th>
                </tr>
              </thead>
              <tbody>
                {(job.destinations || []).map((d, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{d.label}</td>
                    <td><StatusLabel status={d.state} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{d.last_at ? ago(d.last_at) : '—'}</td>
                    <td>{d.copies != null ? `${d.copies.toLocaleString()}${d.unit ? ` ${d.unit}` : ''}` : '—'}</td>
                    <td>{fmtBytes(d.bytes)}</td>
                    <td style={{ color: 'var(--c-text-muted)' }}>{d.retention || '—'}{d.note ? ` · ${d.note}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BackupsOverviewWidget() {
  const { t } = useTranslation();
  const ago = useAgo();
  const qc = useQueryClient();
  const [openKey, setOpenKey] = useState(null);
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['backups-overview'],
    queryFn: () => api.get('/backups/overview').then(r => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  const overall = data?.overall || 'unknown';
  const problems = (data?.jobs || []).filter(j => j.status !== 'ok').length;
  const HeadIcon = overall === 'ok' ? ShieldCheck : ShieldAlert;

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <HeadIcon size={18} color={STATUS_COLOR[overall]} /> {t('dashboard.widgets.backupsOverview')}
          {data && (
            <span style={{
              fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
              color: STATUS_COLOR[overall], border: `1px solid ${STATUS_COLOR[overall]}`,
            }}>
              {overall === 'ok' ? t('dashboard.widgets.backupsAllGood') : t('dashboard.widgets.backupsToReview', { n: problems || 1 })}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--c-text-muted)' }}>
          {data?.inventory?.generated_at && (
            <span style={{ color: data.inventory.status === 'stale' ? STATUS_COLOR.stale : undefined }}>
              {t('dashboard.widgets.backupsChecked', { ago: ago(data.inventory.generated_at) })}
            </span>
          )}
          <button className="btn btn-ghost btn-icon btn-sm" title={t('common.refresh', 'Refresh')}
            onClick={() => qc.invalidateQueries({ queryKey: ['backups-overview'] })}>
            <RefreshCw size={14} className={isFetching ? 'spin' : undefined} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>{t('common.loading')}</div>
      ) : isError || !data ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>{t('common.actionFailed')}</div>
      ) : (
        <div style={{ padding: '4px 16px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data.inventory?.status === 'stale' && (
            <div style={{ fontSize: 12, color: STATUS_COLOR.stale, display: 'flex', gap: 6, alignItems: 'center' }}>
              <AlertTriangle size={13} /> {t('dashboard.widgets.backupsCollectorStale')}
            </div>
          )}
          <StorageCard storage={data.storage} />

          {(data.checks || []).map(c => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
              <FlaskConical size={14} color={STATUS_COLOR[c.status]} />
              <span style={{ fontWeight: 600 }}>{c.label}</span>
              <StatusLabel status={c.status} />
              <span style={{ color: 'var(--c-text-muted)' }}>
                · {ago(c.last_at)}{c.detail ? ` · ${c.detail}` : ''}
              </span>
            </div>
          ))}

          <div>
            {(data.jobs || []).map(job => (
              <JobRow key={job.key} job={job} open={openKey === job.key}
                onToggle={() => setOpenKey(k => (k === job.key ? null : job.key))} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
